const { getRoleGroup } = require('user-utils');

const factory = (GatewayApi, Model, Relation, Uuid, AvatarUrl, Document, _) => {
  class Message extends Model {
    $url = () => `messages/${this.context.personId}/${this.id}`;

    $postUrl = () => `messages/${this.context.personId}`;

    $fetch() {}

    $getAvatar(size = 40) {
      return this.$isAuthorSystem()
        ? AvatarUrl.getAvatar('help@bench.co', size)
        : this.$author().$avatarURL(size);
    }

    $getAuthorFirstNameAndInitial() {
      return `${this.$author().firstName} ${this.$author().lastName[0]}.`;
    }

    $getAuthorFirstNameAndInitialFromFullName = () => {
      if (this.$author().fullName.indexOf(' ') !== -1) {
        const indexAfterInitial = this.$author().fullName.indexOf(' ') + 2;
        return `${this.$author().fullName.substring(0, indexAfterInitial)}.`;
      }
      return this.$author().fullName;
    };

    $getName() {
      if (this.$isAuthorSystem()) return 'Bench';
      if (this.$author().firstName && this.$author().lastName)
        return this.$getAuthorFirstNameAndInitial();
      if (this.$author().fullName) return this.$getAuthorFirstNameAndInitialFromFullName();
      return '[Name not found]';
    }

    $authorRoles() {
      if (!this.context || !this.context.eventSource) return [];
      return this.context.eventSource.authorRoles;
    }

    $authorGroup = () => getRoleGroup(this.$authorRoles());

    $isAuthorBench = () => this.$authorGroup() === 'BENCH';

    $isAuthorClient = () => ['CLIENT', 'TAX_ACCOUNTANT'].includes(this.$authorGroup());

    sentWithSms = () => {
      const smsAsset = _.find(
        this.assets,
        asset => asset.assetType === 'payload' && asset.payloadName === 'sms',
      );
      const sms = (smsAsset && smsAsset.payload) || null;
      return (sms && sms.send) || false;
    };

    $isAuthorSystem() {
      if (!this.context || !this.context.eventSource) return false;
      const eventSource = this.context.eventSource;
      return eventSource.sourceType === 'System' && eventSource.component !== 'dropbox';
    }

    $isAuthor(id) {
      if (!this.context || !this.context.eventSource) return false;

      const authorId = this.context.eventSource.authorId;
      return authorId === id;
    }

    $isViewed(viewerId) {
      if (this.name === 'feed.receipt.created') return true;

      return _.reduce(
        this.actions,
        (viewed, action) =>
          viewed || (action.actionName === 'viewed' && action.personId === viewerId),
        false,
      );
    }

    $serialize(data) {
      const payload = _(data.assets)
        .filter({ assetType: 'payload' })
        .keyBy('payloadName')
        .mapValues('payload')
        .value();

      const resources = _(data.assets)
        .filter({ assetType: 'resource' })
        .keyBy('resource')
        .mapValues('resourceId')
        .value();

      const assets =
        data.assets != null
          ? data.assets.map(asset => {
              if (asset.assetType === 'resource' && asset.resource === 'document') {
                // eslint-disable-next-line no-param-reassign
                asset.$document = () => new Document(asset.resourceId);
              }
              return asset;
            })
          : undefined;

      const { context } = data;
      if (
        context.eventSource.component === 'dropbox' &&
        context.eventSource.sourceType === 'System'
      ) {
        context.eventSource.authorId = context.personId;
        context.eventSource.authorRoles.push('CLIENT');
      }

      return _.assignIn(data, { payload, resources, assets });
    }

    $getAttachedTransaction() {
      if (this.name !== 'feed.comment.transaction') return;

      const payload = _.find(this.assets, { resource: 'document' });
      if (!payload) return;

      return new Document(payload.resourceId);
    }

    $deserialize(data) {
      const { context } = data;
      if (
        context.eventSource.component === 'dropbox' &&
        context.eventSource.sourceType === 'System'
      ) {
        delete context.eventSource.authorId;
        _.pull(context.eventSource.authorRoles, 'CLIENT');
      }

      return super.$deserialize(_.omit(data, ['payload', 'resources']));
    }

    $getResourceId(resource) {
      const result = _.find(this.assets, { resource });
      return result.resourceId;
    }

    static $generate({
      viewerId,
      clientId,
      roles,
      sourceType,
      component,
      assets,
      name,
      inboxState,
    }) {
      let eventSource;
      const eventId = Uuid.generate();
      const createdAt = new Date().getTime();

      if (sourceType === 'System') {
        eventSource = {
          sourceType,
          component,
          authorRoles: [],
        };
      } else {
        eventSource = {
          sourceType: sourceType || 'Person',
          component: 'messaging',
          authorId: viewerId,
          authorRoles: roles,
        };
      }

      const data = {
        id: eventId,
        createdAt,
        version: '1.0.0',
        name: name || 'feed.message.created',
        context: {
          personId: clientId,
          eventSource,
          inboxState,
        },
        assets: assets || [],
        actions: [],
        placeholder: false,
      };

      return new Message(Message.prototype.$serialize(data));
    }

    addPayload({ payloadName, payload }) {
      this.$set({ assets: this.assets.concat([{ assetType: 'payload', payloadName, payload }]) });
      return this;
    }

    addResource({ resource, resourceId }) {
      this.$set({ assets: this.assets.concat([{ assetType: 'resource', resource, resourceId }]) });
      return this;
    }

    addDocument(id) {
      return this.addResource({
        resource: 'document',
        resourceId: id,
      });
    }

    addMessage(message) {
      this.addPayload({
        payloadName: 'message',
        payload: message,
      });
      return this;
    }

    setAsPlaceholder(isPlaceholder = true) {
      return this.$set({ placeholder: isPlaceholder });
    }

    addAction({ actionName, personId, roles }) {
      const createdAt = new Date().getTime();

      this.$set({
        actions: this.actions.concat([
          {
            id: Uuid.generate(),
            eventId: this.id,
            createdAt,
            actionName,
            personId,
            groupName: getRoleGroup(roles),
          },
        ]),
      });

      return this;
    }

    /**
     * Edits a message in the model by reference
     * @param  {String} message payload
     * @return {Object} message object
     */
    $put(text) {
      const name = 'feed.message.edited';
      // For message the personId=clientId
      const clientId = this.context.personId;
      const viewerId = _.get(this.context, 'eventSource.authorId') || 0;
      const roles = _.get(this.context, 'eventSource.authorRoles') || [];

      const message = Message.$generate({
        viewerId,
        clientId,
        roles,
        name,
      })
        .$markAsViewed({
          viewerId,
          roles,
        })
        .addMessage(text)
        .addResource({
          resource: 'message',
          resourceId: this.id,
        });

      return super.$put(message.$data());
    }

    /**
     * Performs a delete request and destroys the current model
     * @return {Func} $apiCall
     */
    $delete() {
      let authorId = 0;

      if (this.context && this.context.eventSource) {
        authorId = this.context.eventSource.authorId;
      }

      return super.$delete({ authorId });
    }

    $post() {
      this.$failed = false;
      this.$inProgress = true;

      super
        .$post(this.$data())
        .then(() => (this.$inProgress = false))
        .catch(err => {
          this.$resolved = true;
          this.$inProgress = false;
          this.$failed = true;
          return err;
        });

      return this;
    }

    // TODO: do we need to generate complete action objects?
    // perhaps we need to check the author existing viewed actions
    // to determine if it should be marked for the current user
    $markAsViewed({ viewerId, roles }) {
      if (!this.$isViewed(viewerId)) {
        this.addAction({
          actionName: 'viewed',
          personId: viewerId,
          roles,
        });
      }
      return this;
    }

    /**
     * Filters actions to get read receipts by BKs and viewed by clients
     * @return {Array}
     */
    getReadReceipts() {
      return this.actions
        .filter(action => action.actionName === 'viewed')
        .filter(action => action.personId !== this.context.eventSource.authorId);
    }

    /**
     * Checks if there's at least one read receipt and is a BK
     * @return {Boolean}
     */
    hasReadReceipt() {
      return this.getReadReceipts().length > 0;
    }
  }

  Object.assign(Message.prototype, {
    id: null,
    $apiService: GatewayApi,
    $author: Relation('Person', 'context.eventSource.authorId'),
    $failed: false,
  });

  return Message;
};

const moduleDeps = [
  require('data/util').name,
  require('data/model/person').name,
  require('data/model/document').name,
];

module.exports = angular.module('bench.data.model.message', moduleDeps).factory('Message', factory);
